import { useEffect, useRef, useState, useCallback } from "react";
import { X, Play, Pause } from "lucide-react";

const SCENES = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/1e9bcdb0-5354-49ae-a51a-d75ee6e20ee0/images/d64875b3f72a29572d4662abba6c93243082d8684578ae8fd59fbe21ba285431.jpeg",
    eyebrow: "The Ikigora Story",
    title: "Every great leap starts with a spark.",
    sub: "An idea, a question, a challenge worth solving.",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/1e9bcdb0-5354-49ae-a51a-d75ee6e20ee0/images/82e906afe321fd8b70e60517c02ab5c9890dba1ba3c397a4cef40930bf88d495.jpeg",
    eyebrow: "Connect",
    title: "Leaders don't build alone.",
    sub: "A global network of founders, CTOs and AI pioneers — learning in the open.",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/1e9bcdb0-5354-49ae-a51a-d75ee6e20ee0/images/5b3a36fac9b5fd06eeb55affbdb5db1839713d2695c7fc767a8c37abd890ca49.jpeg",
    eyebrow: "Learn & Build",
    title: "Knowledge shared. Ideas made real.",
    sub: "Courses, live projects and shared workspaces where teams build at the frontier.",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/1e9bcdb0-5354-49ae-a51a-d75ee6e20ee0/images/496ce003492c5683f35b33617198c11877dbe7a1759d0338c4afbdb64edb0c47.jpeg",
    eyebrow: "Ikigora",
    title: "A stronger tomorrow, built together.",
    sub: "People · Ideas · Opportunities",
    logo: true,
  },
];

const SCENE_MS = 4200;

export function IntroVideoModal({ open, onClose, onJoin }) {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const raf = useRef(null);
  const startRef = useRef(0);

  const step = useCallback((ts) => {
    if (!startRef.current) startRef.current = ts;
    const p = Math.min((ts - startRef.current) / SCENE_MS, 1);
    setProgress(p);
    if (p >= 1) {
      startRef.current = 0;
      setProgress(0);
      setI((prev) => (prev + 1) % SCENES.length);
    }
    raf.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    if (!open || !playing) return;
    startRef.current = 0;
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [open, playing, i, step]);

  useEffect(() => {
    if (open) {
      setI(0); setProgress(0); setPlaying(true);
      startRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const scene = SCENES[i];

  const togglePlay = () => setPlaying((v) => !v);

  const goTo = (idx) => {
    setI(idx); setProgress(0);
    startRef.current = 0;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy/90 backdrop-blur-md p-4"
      onClick={onClose} data-testid="intro-video-modal">
      <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black"
        onClick={(e) => e.stopPropagation()}>
        {SCENES.map((s, idx) => (
          <div key={idx}
            className="absolute inset-0 transition-opacity duration-700 ease-out"
            style={{ opacity: idx === i ? 1 : 0 }}>
            <img src={s.img} alt=""
              className="w-full h-full object-cover"
              style={{
                transform: idx === i ? `scale(${1.06 + progress * 0.08})` : "scale(1.06)",
                transition: "transform 4200ms linear",
              }} />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/10" />
          </div>
        ))}

        {/* caption */}
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 text-white" key={i}>
          {scene.logo && (
            <img src="/ikigora-icon.png" alt="Ikigora" className="w-12 h-12 object-contain mb-4 fade-up" />
          )}
          <div className="eyebrow text-electric fade-up">{scene.eyebrow}</div>
          <h2 className="mt-2 font-serif font-extrabold text-2xl sm:text-4xl leading-tight max-w-2xl fade-up"
            style={{ animationDelay: "0.08s" }} data-testid="intro-scene-title">
            {scene.title}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/70 max-w-xl fade-up" style={{ animationDelay: "0.16s" }}>
            {scene.sub}
          </p>
          {scene.logo && onJoin && (
            <button onClick={onJoin} data-testid="intro-join-button"
              className="mt-5 inline-flex items-center gap-2 bg-cobalt text-white px-5 py-2.5 text-sm font-semibold rounded-md hover:bg-gold-hover transition-colors fade-up"
              style={{ animationDelay: "0.24s" }}>
              Join the conversation
            </button>
          )}
        </div>

        {/* top bar: progress + close */}
        <div className="absolute top-0 inset-x-0 p-4 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-1.5" data-testid="intro-progress">
            {SCENES.map((_, idx) => (
              <button key={idx} onClick={() => goTo(idx)}
                className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
                <span className="block h-full bg-white rounded-full"
                  style={{ width: idx < i ? "100%" : idx === i ? `${progress * 100}%` : "0%" }} />
              </button>
            ))}
          </div>
          <button onClick={onClose} data-testid="intro-close-button"
            className="grid place-items-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* play/pause */}
        <button onClick={togglePlay} data-testid="intro-playpause-button"
          className="absolute bottom-5 right-5 grid place-items-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur-sm">
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
      </div>
    </div>
  );
}
